import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getSearchLiveTv, getSearchMovie, getSearchSportsVod, getSearchTvShow } from '@/api/index.js';
import { SEARCH_DEFAULT_LIMIT, SEARCH_LOAD_STEP, SEARCH_MAX_LIMIT } from '@/constants/search';
import { SEARCH_CONTENT_TABS, SEARCH_ROUTE_QUERY, SEARCH_TAB_OPTIONS, SEARCH_TABS } from '@/views/Search/constants';

const searchRequests = {
  [SEARCH_TABS.movies]: getSearchMovie,
  [SEARCH_TABS.tvShows]: getSearchTvShow,
  [SEARCH_TABS.sportsVod]: getSearchSportsVod,
  [SEARCH_TABS.liveTv]: getSearchLiveTv,
};

const isSearchTab = (tab) => Object.values(SEARCH_TABS).includes(tab);

const getContentTabs = (tab) => (tab === SEARCH_TABS.all ? SEARCH_CONTENT_TABS : [tab]);

const parseRouteTab = (value) => (value === undefined ? NaN : Number(value));

export const useSearchPage = () => {
  const route = useRoute();
  const router = useRouter();

  const routeTab = parseRouteTab(route.query[SEARCH_ROUTE_QUERY.tab]);

  const query = ref(route.query[SEARCH_ROUTE_QUERY.query] ?? '');
  const searchedQuery = ref('');
  const activeTab = ref(isSearchTab(routeTab) ? routeTab : SEARCH_TABS.all);
  const results = ref({});
  const resultQueries = ref({});
  const limits = ref({});
  const loadingTabs = ref({});
  const loadingMore = ref(false);

  const isAllTab = computed(() => activeTab.value === SEARCH_TABS.all);
  const activeTabOption = computed(() => SEARCH_TAB_OPTIONS.find(({ id }) => id === activeTab.value));
  const fallbackTabOption = computed(() => (isAllTab.value ? null : SEARCH_TAB_OPTIONS.find(({ id }) => id === SEARCH_TABS.all)));
  const activeResults = computed(() => (isAllTab.value ? [] : results.value[activeTab.value] ?? []));

  const activeSections = computed(() => SEARCH_CONTENT_TABS.map((tab) => ({ tab, items: results.value[tab] ?? [] })).filter(({ items }) => isAllTab.value && items.length));

  const resultsCount = computed(() => (isAllTab.value ? activeSections.value.reduce((count, { items }) => count + items.length, 0) : activeResults.value.length));

  const loading = computed(() => getContentTabs(activeTab.value).some((tab) => loadingTabs.value[tab]));

  const status = computed(() => {
    if (!searchedQuery.value) return 'idle';
    if (loading.value && !resultsCount.value) return 'loading';
    return resultsCount.value ? 'results' : 'empty';
  });

  const hasNextPage = computed(() => {
    if (isAllTab.value) return false;

    const limit = limits.value[activeTab.value];

    return activeResults.value.length >= limit && limit < SEARCH_MAX_LIMIT;
  });

  const syncRoute = () => {
    const routeQuery = searchedQuery.value ? { [SEARCH_ROUTE_QUERY.query]: searchedQuery.value, [SEARCH_ROUTE_QUERY.tab]: activeTab.value } : {};

    router.replace({ query: routeQuery });
  };

  const fetchResults = async (tab) => {
    const requestedQuery = searchedQuery.value;
    const { data } = await searchRequests[tab](requestedQuery, limits.value[tab]);

    if (resultQueries.value[tab] === requestedQuery) results.value[tab] = data.data ?? [];
  };

  const searchTab = async (tab) => {
    loadingTabs.value[tab] = true;
    results.value[tab] = null;
    resultQueries.value[tab] = searchedQuery.value;
    limits.value[tab] = SEARCH_DEFAULT_LIMIT;

    try {
      await fetchResults(tab);
    } catch {
      results.value[tab] = [];
    } finally {
      loadingTabs.value[tab] = false;
    }
  };

  const searchActiveTab = () => {
    if (!searchedQuery.value) return;

    getContentTabs(activeTab.value)
      .filter((tab) => resultQueries.value[tab] !== searchedQuery.value)
      .forEach(searchTab);
  };

  const submit = () => {
    searchedQuery.value = query.value.trim();
    results.value = {};
    resultQueries.value = {};
    syncRoute();
    searchActiveTab();
  };

  const setActiveTab = (tab) => {
    activeTab.value = tab;
    syncRoute();
    searchActiveTab();
  };

  const clear = () => {
    query.value = '';
    submit();
  };

  const loadMore = async () => {
    const tab = activeTab.value;

    loadingMore.value = true;
    limits.value[tab] = Math.min(limits.value[tab] + SEARCH_LOAD_STEP, SEARCH_MAX_LIMIT);

    try {
      await fetchResults(tab);
    } catch {
      limits.value[tab] = SEARCH_MAX_LIMIT;
    } finally {
      loadingMore.value = false;
    }
  };

  watch(
    () => route.query[SEARCH_ROUTE_QUERY.tab],
    (value) => {
      const tab = parseRouteTab(value);

      if (isSearchTab(tab) && tab !== activeTab.value) setActiveTab(tab);
    }
  );

  onMounted(() => {
    if (query.value) submit();
  });

  return {
    query,
    searchedQuery,
    activeTab,
    isAllTab,
    activeTabOption,
    fallbackTabOption,
    activeResults,
    activeSections,
    resultsCount,
    status,
    hasNextPage,
    loading,
    loadingMore,
    submit,
    setActiveTab,
    clear,
    loadMore,
  };
};
